import React, { useMemo, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import MetricIndicator from "@acl-services/sriracha-metrics/dist/Metrics";
// TODO: Import or implement GlobalValueFormatter and HighChartLabelSizer utilities as needed
import UrlFlipper from "../../services/urlFlipper/urlFlipper";
import HighChartLabelSizer from "../../../../visualizer/js/modules/visualization/highCharts/highChartLabelSizer";
import GlobalValueFormatter from "../../services/formatters/globalValueFormatter";


/**
 * Migrated from indicator.directive.js (AngularJS)
 * This component prepares props and renders the MetricIndicator React component.
 * Now includes event handling for 'chartRedraw' (calls reflow on ref).
 */
const Indicator = ({
  fontZoomRatio,
  metricModel,
  presentationMode,
  sparklineModel,
  showOnlyThreshold,
  showInsufficientSparklineDataMessage,
  locale,
}) => {
  // Prepare/normalize data as needed (mimicking AngularJS controller logic)
  const metric = useMemo(() =>
    metricModel && typeof metricModel.toJson === 'function'
      ? metricModel.toJson()
      : metricModel,
    [metricModel]
  );
  const metricSparkline = useMemo(() =>
    sparklineModel && typeof sparklineModel.toJson === 'function'
      ? sparklineModel.toJson()
      : sparklineModel,
    [sparklineModel]
  );

  const timezoneOffsetSeconds = GlobalValueFormatter.getTimezoneOffset().utcOffsetSeconds();

  // Create an instance of HighChartLabelSizer
  const highChartLabelSizer = HighChartLabelSizer();

  // Sync with indicator.directive.js: use HighChartLabelSizer.updateLabelFontSize with correct args
  // NOTE: HighChartLabelSizer must be imported or available in scope
  // The configPath is hardcoded here to match the AngularJS version
  const configPath = [
    "plotOptions.series.dataLabels.style.fontSize",
    "tooltip.style.fontSize",
    "xAxis.labels.style.fontSize",
    "yAxis.labels.style.fontSize",
    "yAxis.plotLines.label.style.fontSize",
  ];
  // Disable animation for exporting as pdf
  const hcConfig = {
    plotOptions: {
      series: {
        animation: false,
      },
    },
  };
  const fontConfig = highChartLabelSizer.updateLabelFontSize(
    UrlFlipper.isRenderMode("export") ? hcConfig : {},
    presentationMode ? "large" : undefined,
    configPath,
    fontZoomRatio
  );

  const ref = useRef();

  // Event handler for chartRedraw (matches AngularJS $scope.$on logic)
  const handleChartRedraw = useCallback(() => {
    if (ref.current && typeof ref.current.reflow === "function") {
      ref.current.reflow();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("chartRedraw", handleChartRedraw);
    return () => {
      window.removeEventListener("chartRedraw", handleChartRedraw);
    };
  }, [handleChartRedraw]);

  return (
    <div className="metric-indicator__container" style={{ height: "100%" }}>
      <MetricIndicator
        className="sriracha"
        style={{ height: "100%", display: "block" }}
        hcFontConfig={fontConfig}
        metric={metric}
        metricSparkline={metricSparkline}
        showSparkline={metric && metric.show_sparkline}
        showOnlyThreshold={showOnlyThreshold}
        showInsufficientSparklineDataMessage={showInsufficientSparklineDataMessage}
        locale={locale}
        timezoneOffsetSeconds={timezoneOffsetSeconds}
        ref={ref}
      />
    </div>
  );
};

Indicator.propTypes = {
  fontZoomRatio: PropTypes.any,
  metricModel: PropTypes.any,
  presentationMode: PropTypes.any,
  sparklineModel: PropTypes.any,
  showOnlyThreshold: PropTypes.any,
  showInsufficientSparklineDataMessage: PropTypes.any,
  locale: PropTypes.any,
};

export default Indicator; 